import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfigurationFieldRenderer } from "@/ui/configurationFieldRenderer";
import { IntegrationIcon } from "@/ui/componentSidebar/integrationIcons";
import { IntegrationInstructions } from "@/ui/IntegrationInstructions";
import { getIntegrationTypeDisplayName } from "@/lib/integrationDisplayName";
import { getApiErrorMessage } from "@/lib/errors";
import { getUsageLimitNotice, getUsageLimitToastMessage } from "@/lib/usageLimits";
import { getIntegrationWebhookUrl } from "@/lib/integrationUtils";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import { useUpdateIntegration } from "@/hooks/useIntegrations";
import { UsageLimitAlert } from "@/components/UsageLimitAlert";
import { Alert, AlertDescription, AlertTitle } from "@/ui/alert";
import { selectCreateStepFields, selectVisibleFields, selectWebhookStepFields } from "./configurationFields";
import { createWithGeneratedName, useGeneratedIntegrationName } from "./generatedName";
import { IntegrationCreateDialogFooter } from "./IntegrationCreateDialogFooter";
import { useBrowserActionSetup } from "./useBrowserActionSetup";
import type {
  ConfigurationField,
  IntegrationsIntegrationDefinition,
  OrganizationsBrowserAction,
  OrganizationsCreateIntegrationResponse,
} from "@/api-client";

export type IntegrationCreatePayload = {
  integrationName: string;
  name: string;
  configuration?: Record<string, unknown>;
};

export interface IntegrationCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integrationDefinition: IntegrationsIntegrationDefinition | null | undefined;
  organizationId: string;
  /** Called to create the integration. Returns the API response (integration id, browser action, webhook url, etc.). */
  onCreateIntegration: (payload: IntegrationCreatePayload) => Promise<OrganizationsCreateIntegrationResponse>;
  /** Optional: called when dialog closes so parent can reset mutation state. */
  onReset?: () => void;
  defaultName?: string;
  integrationHomeHref?: string;
  onCreated?: (integrationId: string, instanceName: string) => void;
  /** Called when create returns a capability-based setup step that must continue in the setup wizard. */
  onCapabilitySetupRequired?: (integrationName: string, integrationId: string) => void;
  /** If set, instructions are truncated at this heading (e.g. "## Webhook integration") so only the part before is shown in the create step. */
  instructionsEndBeforeHeading?: string;
  /** If set, only these configuration field names are shown in the initial create step; the rest are shown in the webhook completion step. */
  initialStepFieldNames?: string[];
  /** Configuration field names this flow never shows, e.g. optional keys that would distract during onboarding. */
  hiddenFieldNames?: string[];
  /** Optional custom description for the webhook completion step. */
  webhookStepDescription?: ReactNode;
  /** Pre-created integration state for resuming a flow started inline (e.g. browser action after inline creation). */
  initialCreatedIntegrationId?: string;
  initialBrowserAction?: OrganizationsBrowserAction;
  initialWebhookSetup?: { id: string; webhookUrl: string; config: Record<string, unknown> };
  /** Existing configuration to pre-populate when resuming a pending integration flow. */
  initialConfiguration?: Record<string, unknown>;
  /** Where to send the browser after the external provider redirects back (e.g. workspace setup). */
  setupReturnTo?: string;
  /** Names already taken in the organization, used to keep generated names unique. */
  existingIntegrationNames?: Set<string>;
}

const NO_EXISTING_NAMES: Set<string> = new Set();
const NO_HIDDEN_FIELDS: string[] = [];

export function IntegrationCreateDialog({
  open,
  onOpenChange,
  integrationDefinition,
  organizationId,
  onCreateIntegration,
  onReset,
  defaultName = "",
  integrationHomeHref,
  onCreated,
  onCapabilitySetupRequired,
  instructionsEndBeforeHeading,
  initialStepFieldNames,
  hiddenFieldNames = NO_HIDDEN_FIELDS,
  webhookStepDescription,
  initialCreatedIntegrationId,
  initialBrowserAction,
  initialWebhookSetup,
  initialConfiguration,
  setupReturnTo,
  existingIntegrationNames = NO_EXISTING_NAMES,
}: IntegrationCreateDialogProps) {
  const [integrationName, setIntegrationName] = useState(defaultName);
  const [configuration, setConfiguration] = useState<Record<string, unknown>>({});
  const [pendingWebhookSetup, setPendingWebhookSetup] = useState<{
    id: string;
    webhookUrl: string;
    config: Record<string, unknown>;
  } | null>(null);
  const [isCreatePending, setIsCreatePending] = useState(false);
  const [createError, setCreateError] = useState<unknown>(null);
  const [createdIntegrationId, setCreatedIntegrationId] = useState<string | undefined>(undefined);
  const prevOpenRef = useRef(false);

  const resolvedIntegrationId = pendingWebhookSetup?.id ?? createdIntegrationId ?? initialCreatedIntegrationId;
  const updateIntegrationMutation = useUpdateIntegration(organizationId, resolvedIntegrationId ?? "");

  const selectedInstructions = useMemo(() => {
    const raw = integrationDefinition?.instructions?.trim();
    if (!raw || !instructionsEndBeforeHeading) return raw ?? "";
    const idx = raw.indexOf(instructionsEndBeforeHeading);
    return idx >= 0 ? raw.slice(0, idx).trim() : raw;
  }, [integrationDefinition?.instructions, instructionsEndBeforeHeading]);

  const { createStepFields, webhookStepFields } = useMemo(() => {
    const visibleFields = selectVisibleFields(integrationDefinition?.configuration, hiddenFieldNames);
    return {
      createStepFields: selectCreateStepFields(visibleFields, initialStepFieldNames),
      webhookStepFields: selectWebhookStepFields(visibleFields, initialStepFieldNames),
    };
  }, [integrationDefinition?.configuration, hiddenFieldNames, initialStepFieldNames]);

  const isGitHub = integrationDefinition?.name === "github";
  const {
    name: effectiveIntegrationName,
    baseName: githubBaseName,
    setCreatedName,
  } = useGeneratedIntegrationName({
    isGitHub,
    typedName: integrationName,
    owner: configuration.organization,
    existingNames: existingIntegrationNames,
  });

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setIntegrationName("");
        setConfiguration({});
        setPendingWebhookSetup(null);
        setCreatedIntegrationId(undefined);
        setCreatedName(undefined);
        setCreateError(null);
        onReset?.();
      }
      onOpenChange(next);
    },
    [onOpenChange, onReset, setCreatedName],
  );

  const handleClose = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const {
    browserAction: createIntegrationBrowserAction,
    browserActionCompleted,
    setBrowserAction: setCreateIntegrationBrowserAction,
    resetBrowserAction,
    continueBrowserAction: handleBrowserActionContinue,
    saveBrowserActionConfig: handleBrowserActionConfigSave,
    finishBrowserActionSetup: handleFinishBrowserActionSetup,
  } = useBrowserActionSetup({
    updateIntegrationMutation,
    configuration,
    organizationId,
    integrationId: resolvedIntegrationId,
    integrationName: effectiveIntegrationName,
    returnTo: setupReturnTo,
    onCreated,
    handleClose,
  });

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    setIntegrationName(defaultName);
    setConfiguration(initialConfiguration ? { ...initialConfiguration } : {});
    resetBrowserAction(initialBrowserAction ?? undefined);
    setPendingWebhookSetup(initialWebhookSetup ?? null);
    setCreatedIntegrationId(initialCreatedIntegrationId ?? undefined);
    setCreatedName(undefined);
  }, [
    open,
    defaultName,
    initialBrowserAction,
    initialWebhookSetup,
    initialCreatedIntegrationId,
    initialConfiguration,
    resetBrowserAction,
    setCreatedName,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!integrationDefinition?.name || !organizationId) return;
    const definitionName = integrationDefinition.name;
    const nextName = effectiveIntegrationName.trim();
    if (!nextName) {
      showErrorToast("Integration name is required");
      return;
    }

    setCreateError(null);
    setIsCreatePending(true);
    try {
      const created = isGitHub
        ? await createWithGeneratedName({
            baseName: githubBaseName,
            takenNames: existingIntegrationNames,
            create: (name) => onCreateIntegration({ integrationName: definitionName, name, configuration }),
          })
        : {
            result: await onCreateIntegration({ integrationName: definitionName, name: nextName, configuration }),
            name: nextName,
          };

      setCreatedName(created.name);
      const integration = created.result.integration;
      const browserAction = integration?.status?.browserAction;
      const webhookUrl = getIntegrationWebhookUrl(integration?.status?.metadata);

      // Capability-based integrations (e.g. GitHub) return a setup wizard step, not a
      // browserAction. Hand off to the setup wizard instead of closing into a dead end.
      if (integration?.status?.setupState?.currentStep && integration.metadata?.id) {
        const setupName = integration.metadata.integrationName || integrationDefinition.name;
        onCapabilitySetupRequired?.(setupName, integration.metadata.id);
        handleClose();
        return;
      }

      if (browserAction) {
        setCreateIntegrationBrowserAction(browserAction);
        if (integration?.metadata?.id) {
          setCreatedIntegrationId(integration.metadata.id);
        }
        return;
      }
      if (integration?.metadata?.id && webhookUrl) {
        setPendingWebhookSetup({
          id: integration.metadata.id,
          webhookUrl,
          config: { ...configuration },
        });
        return;
      }
      handleClose();
      if (integration?.metadata?.id) {
        onCreated?.(integration.metadata.id, integration.metadata.name || created.name);
      }
    } catch (error) {
      setCreateError(error);
      showErrorToast(getUsageLimitToastMessage(error, "Failed to create integration"));
    } finally {
      setIsCreatePending(false);
    }
  }, [
    integrationDefinition?.name,
    organizationId,
    effectiveIntegrationName,
    configuration,
    existingIntegrationNames,
    githubBaseName,
    isGitHub,
    onCreateIntegration,
    handleClose,
    onCreated,
    onCapabilitySetupRequired,
    setCreateIntegrationBrowserAction,
    setCreatedName,
  ]);

  const handleCompleteWebhookSetup = useCallback(async () => {
    if (!pendingWebhookSetup) return;

    try {
      await updateIntegrationMutation.mutateAsync({
        configuration: { ...pendingWebhookSetup.config, ...configuration },
      });
      handleClose();
      onCreated?.(pendingWebhookSetup.id, effectiveIntegrationName);
    } catch {
      showErrorToast("Failed to complete setup");
    }
  }, [pendingWebhookSetup, configuration, updateIntegrationMutation, handleClose, onCreated, effectiveIntegrationName]);

  if (!integrationDefinition) return null;

  const displayName =
    getIntegrationTypeDisplayName(undefined, integrationDefinition.name) || integrationDefinition.name;
  const createErrorNotice = createError ? getUsageLimitNotice(createError, organizationId) : null;
  const resolvedHomeHref =
    resolvedIntegrationId && organizationId
      ? `/${organizationId}/settings/integrations/${resolvedIntegrationId}`
      : integrationHomeHref;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[80vh] overflow-y-auto border-0 dark:border dark:border-gray-700/70"
        showCloseButton={!isCreatePending && !updateIntegrationMutation.isPending}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <IntegrationIcon
              integrationName={integrationDefinition.name}
              iconSlug={integrationDefinition.icon}
              className="h-6 w-6 text-gray-500 dark:text-gray-400"
            />
            <div className="flex items-center gap-2">
              <DialogTitle>{pendingWebhookSetup ? "Complete webhook setup" : `Configure ${displayName}`}</DialogTitle>
              {resolvedHomeHref && (
                <a
                  href={resolvedHomeHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-4 w-4 items-center justify-center text-gray-500 hover:text-gray-800 transition-colors"
                  aria-label="Open integration settings"
                >
                  <Settings className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
          {!pendingWebhookSetup && (createIntegrationBrowserAction?.description || selectedInstructions) && (
            <IntegrationInstructions
              description={
                browserActionCompleted
                  ? [selectedInstructions, createIntegrationBrowserAction?.description].filter(Boolean).join("\n\n")
                  : ((createIntegrationBrowserAction?.description || selectedInstructions) ?? "")
              }
              className="mt-2"
            />
          )}
        </DialogHeader>

        <div className="space-y-4 ph-no-capture">
          {pendingWebhookSetup ? (
            <>
              {webhookStepDescription ?? (
                <p className="text-sm text-gray-800 dark:text-gray-200">
                  Copy the webhook URL below and complete the required steps in your integration provider. Then enter
                  any required secrets below.
                </p>
              )}
              <div>
                <Label className="text-gray-800 dark:text-gray-100 mb-2">Webhook URL</Label>
                <div className="flex gap-2">
                  <Input type="text" readOnly value={pendingWebhookSetup.webhookUrl} className="font-mono text-sm" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(pendingWebhookSetup.webhookUrl);
                        showSuccessToast("Copied to clipboard");
                      } catch {
                        showErrorToast("Failed to copy to clipboard");
                      }
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>
              {webhookStepFields.map((field: ConfigurationField) => (
                <ConfigurationFieldRenderer
                  key={field.name}
                  field={field}
                  value={configuration[field.name!]}
                  onChange={(value) =>
                    setConfiguration((prev) => ({
                      ...prev,
                      [field.name!]: value,
                    }))
                  }
                  allValues={configuration}
                  organizationId={organizationId}
                />
              ))}
            </>
          ) : (
            <>
              {!browserActionCompleted && !isGitHub && (
                <div>
                  <Label className="text-gray-800 dark:text-gray-100 mb-2">
                    Integration Name
                    <span className="text-gray-800 ml-1">*</span>
                  </Label>
                  <Input
                    type="text"
                    value={integrationName}
                    onChange={(e) => setIntegrationName(e.target.value)}
                    placeholder="e.g., my-app-integration"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">A unique name for this integration</p>
                </div>
              )}
              {createStepFields.length > 0 && (
                <div className="space-y-4">
                  {createStepFields.map((field: ConfigurationField) => {
                    if (!field.name) return null;
                    return (
                      <ConfigurationFieldRenderer
                        key={field.name}
                        field={field}
                        value={configuration[field.name]}
                        onChange={(value) =>
                          setConfiguration((prev) => ({
                            ...prev,
                            [field.name || ""]: value,
                          }))
                        }
                        allValues={configuration}
                        organizationId={organizationId}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <IntegrationCreateDialogFooter
          pendingWebhookSetup={pendingWebhookSetup}
          createIntegrationBrowserAction={createIntegrationBrowserAction}
          browserActionCompleted={browserActionCompleted}
          mutationPending={updateIntegrationMutation.isPending}
          isCreatePending={isCreatePending}
          integrationName={effectiveIntegrationName}
          onCompleteWebhookSetup={handleCompleteWebhookSetup}
          onBrowserActionContinue={handleBrowserActionContinue}
          onBrowserActionConfigSave={handleBrowserActionConfigSave}
          onFinishBrowserActionSetup={handleFinishBrowserActionSetup}
          onSubmit={handleSubmit}
          onClose={handleClose}
        />

        {createError && createErrorNotice ? <UsageLimitAlert notice={createErrorNotice} className="mt-4" /> : null}
        {createError && !createErrorNotice ? (
          <Alert variant="destructive" className="mt-4">
            <AlertTitle>Unable to create integration</AlertTitle>
            <AlertDescription>Failed to create integration: {getApiErrorMessage(createError)}</AlertDescription>
          </Alert>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
